// Hook that manages the "Insert link" modal state, selection restoration,
// and link node insertion into the Lexical editor.

import React, { useState, type ChangeEvent, type ReactNode } from "react";
import {
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  type EditorState,
  type LexicalEditor,
  BaseSelection,
} from "lexical";
import { $createLinkNode, type LinkNode } from "@lexical/link";

import { useModal } from "../../Modal/hooks/useModal";
import { TextInput } from "../../TextInput";
import { LinkModalFields } from "../Styles";

interface UseLinkModalProps {
  editor: LexicalEditor;
  baseId: string;
  baseTestId: string;
}

interface UseLinkModalResult {
  openLinkModal: () => void;
  linkModalElement: ReactNode;
}

export const useLinkModal = (props: UseLinkModalProps): UseLinkModalResult => {
  const [linkText, setLinkText] = useState<string>("");
  const [linkUrl, setLinkUrl] = useState<string>("");
  const [savedSelection, setSavedSelection] = useState<EditorState | null>(null);

  const insertLink = (): void => {
    if (!linkUrl.trim() || !linkText.trim()) {
      return;
    }

    // Restore the selection captured when "Insert link" was clicked,
    // because focusing the modal moves it away from the editor.
    if (savedSelection) {
      props.editor.setEditorState(savedSelection);
    }

    props.editor.update((): void => {
      const selection: BaseSelection | null = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }
      // Match v1's anchor shape (target="_blank" rel="noopener noreferrer"),
      // see eq-author-app/eq-author/src/components/RichTextEditor/LinkPlugin/index.js
      const linkNode: LinkNode = $createLinkNode(linkUrl.trim(), {
        title: linkUrl.trim(),
        target: "_blank",
        rel: "noopener noreferrer",
      });
      linkNode.append($createTextNode(linkText));
      selection.insertNodes([linkNode]);
    });
    props.editor.focus();
  };

  const [triggerModal, linkModalElement] = useModal({
    modalId: `${props.baseId}-link-modal`,
    modalDataTestId: `${props.baseTestId}-link-modal-test`,
    modalTitle: "Insert link",
    modalButtons: [
      {
        id: `${props.baseId}-link-confirm`,
        dataTestId: `${props.baseTestId}-link-confirm-test`,
        text: "Insert",
        variants: "primary",
        onClick: insertLink,
      },
      {
        id: `${props.baseId}-link-cancel`,
        dataTestId: `${props.baseTestId}-link-cancel-test`,
        text: "Cancel",
        variants: "secondary",
      },
    ],
    modalChildren: (
      <LinkModalFields>
        <TextInput
          id={`${props.baseId}-link-text`}
          dataTestId={`${props.baseTestId}-link-text-test`}
          label={{ text: "Text" }}
          value={linkText}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => setLinkText(event.target.value)}
        />
        <TextInput
          id={`${props.baseId}-link-url`}
          dataTestId={`${props.baseTestId}-link-url-test`}
          label={{ text: "Link" }}
          value={linkUrl}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => setLinkUrl(event.target.value)}
        />
      </LinkModalFields>
    ),
  });

  const openLinkModal = (): void => {
    props.editor.getEditorState().read((): void => {
      const selection: BaseSelection | null = $getSelection();
      setLinkText($isRangeSelection(selection) ? selection.getTextContent() : "");
    });
    setSavedSelection(props.editor.getEditorState());
    setLinkUrl("");
    triggerModal();
  };

  return { openLinkModal, linkModalElement };
};
